import { AMBITO_CONJUNTO, AMBITO_NEGOCIO } from "../lib/constants";

/* El menú del panel de aliados.

   HAY UNO POR ÁMBITO y no un menú único recortado por permisos. Podrían
   fundirse —los permisos ya esconderían lo que no toca— pero serían nueve
   entradas de dos oficios que no comparten ni una pantalla, y bastaría un
   permiso mal puesto en el servidor para que a un tendero le apareciera
   "Celadores". Separarlos hace que eso no pueda pasar.

   Dentro de cada uno, el mecanismo es el del panel interno: cada entrada
   declara el permiso que la habilita y `navPara` esconde lo que la persona no
   tiene.

   Sin grupos: son cuatro o cinco entradas. Un encabezado por cada una haría el
   menú más largo que su contenido. */

/* La portería tiene ruta propia y no vive en la raíz.

   Antes sí, con un argumento bueno: quien abre este panel casi siempre lo abre
   para eso. Pero el administrador del conjunto no trabaja en la puerta, y
   aterrizar en un campo de texto con una moto esperando no le dice nada de su
   edificio.

   Se resuelve donde corresponde —en las rutas—: la raíz redirige a Portería si
   es celador y al Resumen si es el administrador. Así ninguno de los dos paga
   por el oficio del otro. */
const NAV_CONJUNTO = [
  {
    group: null,
    items: [
      { to: "/resumen", label: "Resumen", icon: "panel", modulo: "resumen" },
      { to: "/porteria", label: "Portería", icon: "shield", modulo: "porteria" },
      { to: "/entradas", label: "Entradas", icon: "clock", modulo: "entradas" },
      /* «Mi conjunto» va ACÁ y no bajo «Administración», que es donde estaba.

         El celador tiene `perfil` en modo lectura a propósito: las notas de
         portería son instrucciones que necesita a la vista, y el teléfono de la
         administración es a quién llamar cuando algo pasa en la puerta. Con la
         entrada dentro del grupo, a él le aparecía un encabezado
         «Administración» con una sola cosa debajo — y él no administra nada.

         Puesta acá, el grupo de abajo queda siendo lo que dice ser, y a los dos
         les aparece donde les corresponde: al dueño entre lo suyo, al celador
         entre lo que consulta. */
      { to: "/mi-conjunto", label: "Mi conjunto", icon: "building", modulo: "perfil" },
    ],
  },
  {
    /* El grupo aparece sólo cuando hay algo debajo: al celador se le esconden
       las tres entradas y `navPara` descarta el encabezado vacío. */
    group: "Administración",
    items: [
      {
        to: "/residentes",
        label: "Residentes",
        icon: "building",
        modulo: "residentes",
      },
      { to: "/celadores", label: "Celadores", icon: "users", modulo: "celadores" },
      { to: "/reportes", label: "Reportes", icon: "chart", modulo: "reportes" },
    ],
  },
];

/* Los pedidos van en la raíz porque es a lo que se entra.

   Un tendero abre este panel con un cliente esperando: mientras haya uno sin
   aceptar, cualquier otra pantalla que se ofrezca primero es una que estorba.
   Por el mismo motivo el resto va después y en el orden en que se necesita:
   el catálogo se toca a diario, los domiciliarios de vez en cuando, y las
   reseñas y la ficha casi nunca. */
const NAV_NEGOCIO = [
  {
    group: null,
    items: [
      { to: "/", label: "Pedidos", icon: "receipt", modulo: "pedidos" },
      { to: "/productos", label: "Productos", icon: "box", modulo: "productos" },
      {
        to: "/domiciliarios",
        label: "Domiciliarios",
        icon: "bike",
        modulo: "domiciliarios",
      },
      { to: "/torre", label: "Torre de control", icon: "pin", modulo: "torre" },
      { to: "/chat", label: "Conversaciones", icon: "chat", modulo: "chats" },
      { to: "/clientes", label: "Clientes", icon: "users", modulo: "clientes" },
      {
        to: "/promociones",
        label: "Promociones",
        icon: "megaphone",
        modulo: "promociones",
      },
      { to: "/ingresos", label: "Ingresos", icon: "receipt", modulo: "ingresos" },
      { to: "/resenas", label: "Reseñas", icon: "star", modulo: "resenas" },
      { to: "/mi-negocio", label: "Mi negocio", icon: "store", modulo: "negocio" },
      /* La cuenta no es una seccion del panel sino de quien esta entrando,
         pero lleva `modulo` igual: una entrada sin el aparece SIEMPRE, y eso
         le dejaria el menu con una linea a quien no deberia estar en este
         ambito --que es justo lo que `navPara` existe para evitar--. El
         permiso viene en true para todo tendero. */
      { to: "/mi-cuenta", label: "Mi cuenta", icon: "shield", modulo: "cuenta" },
    ],
  },
];

const POR_AMBITO = {
  [AMBITO_CONJUNTO]: NAV_CONJUNTO,
  [AMBITO_NEGOCIO]: NAV_NEGOCIO,
};

/** El menú del ámbito. Vacío mientras no se sabe cuál es: enseñar el del
    conjunto por defecto haría parpadear "Portería" ante un tendero. */
export const navDe = (ambito) => POR_AMBITO[ambito] ?? [];

/** Las entradas que esta persona puede ver, sin grupos vacíos. */
export function navPara(ambito, puedeVer, puedeGestionar) {
  return navDe(ambito)
    .map((seccion) => ({
      ...seccion,
      items: seccion.items.filter((i) => {
        if (!i.modulo) return true;
        if (i.exigeGestion) return puedeGestionar(i.modulo);
        return puedeVer(i.modulo);
      }),
    }))
    .filter((s) => s.items.length > 0);
}

/** La entrada que corresponde a una ruta, DENTRO DE UN ÁMBITO.

    El ámbito no es opcional por comodidad: `/` existe en los dos menús —es la
    portería del celador y los pedidos del tendero— y buscar en ambos devolvía
    la primera que apareciera. Un tendero en la raíz resolvía al módulo
    `porteria`, que no tiene, y el guardia de rutas lo echaba de su propia
    pantalla de inicio.

    Sin ámbito no se adivina: se devuelve nada. Es lo que pasa en el primer
    fotograma, antes de que llegue la sesión, y ahí no hay nada que titular. */
export function entradaDeRuta(pathname, ambito) {
  if (!ambito) return undefined;

  return navDe(ambito)
    .flatMap((s) => s.items)
    .filter((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

export function moduloDeRuta(pathname, ambito) {
  return entradaDeRuta(pathname, ambito)?.modulo ?? null;
}

export function routeTitle(pathname, ambito) {
  return entradaDeRuta(pathname, ambito)?.label ?? "Panel";
}
